import { BuildTags, ErrorTagUtil } from '@onshape/www-inertia';
import errorReporter from '~/utils/AaaSentryIo';

declare global {
  interface Window {
    ptcDebug?: {
      payload?: Record<string, unknown>;
      dataLayerPushes?: Array<Record<string, unknown>>;
      buildTags?: {
        detailed: string;
        pageGeneratedAt: string | null;
        buildTime: string;
        packageVersion: string;
      };
      clientQuery?: Record<
        string,
        {
          query: string;
          params?: Record<string, unknown>;
        }
      >;
      errs?: {
        push(_value: unknown): void;
      };
      _errs?: Array<unknown>;
    };
  }
}

let postedErrorCount = 0;
const MAX_POSTED_ERRORS = 3;

export default defineNuxtPlugin(() => {
  try {
    // if we don't have a window or we already have ptcDebug, we don't need to do anything
    if (!window) {
      return;
    }
    const pageGeneratedAtMetaTag = document.querySelector('meta[name="PGA"]');
    const pageGeneratedAt =
      pageGeneratedAtMetaTag ? pageGeneratedAtMetaTag.getAttribute('content') : null;

    window.ptcDebug = window.ptcDebug || {};
    window.ptcDebug.buildTags = {
      detailed: BuildTags.detailed,
      buildTime: BuildTags.buildTime,
      packageVersion: BuildTags.packageVersion,
      pageGeneratedAt,
    };
    window.ptcDebug.payload = JSON.parse(JSON.stringify(useNuxtApp().payload));
    window.ptcDebug.clientQuery = window.ptcDebug.clientQuery || {};
    const isDevHost =
      window &&
      !window.origin.includes('www.onshape.com') &&
      !window.origin.includes('isocomp') &&
      (window.origin.includes('localhost:') || window.origin.includes('.dev.'));
    if (isDevHost) {
      setInterval(() => {
        if (Object.keys(window.ptcDebug!.clientQuery!).length === 0) {
          return;
        }
        const pre = document.createElement('pre');
        pre.style.position = 'fixed';
        pre.style.top = '0';
        pre.style.left = '0';
        pre.style.width = '100vw';
        pre.style.height = '100vh';
        pre.style.overflow = 'auto';
        pre.style.whiteSpace = 'pre';
        pre.style.margin = '0';
        pre.style.padding = '40px 20px';
        pre.style.background = '#0011a8';
        pre.style.color = '#e5e5e5';
        pre.style.fontFamily = 'Consolas, monospace';
        pre.style.fontSize = '1.2rem';
        pre.style.zIndex = '99999';
        pre.style.boxSizing = 'border-box';
        pre.style.textShadow = '0 0 2px #000, 0 0 8px #0011a8';
        pre.style.letterSpacing = '0.03em';
        pre.style.lineHeight = '1.5';
        pre.innerText = `ERROR: Client side query to Sanity \n ${JSON.stringify(window.ptcDebug!.clientQuery!, null, 2)}`;
        document.body.innerHTML = '';
        document.body.appendChild(pre);
      }, 3000);
    }

    if (window.ptcDebug._errs) {
      return;
    }
    const historicClientErrors = window.ptcDebug.errs || [];
    window.ptcDebug._errs = [];
    window.ptcDebug.errs = {
      push(value: unknown) {
        if (
          errorReporter &&
          postedErrorCount < MAX_POSTED_ERRORS &&
          document.location.hostname.includes('onshape.com')
        ) {
          if (Math.random() < 0.001) {
            // quite for now
            postedErrorCount += 1;
            errorReporter.postToSentry({
              tag: ErrorTagUtil.ptcDebugClientError,
              msg: `value: ${JSON.stringify(value)}`,
            });
          }
        }
        console.error('ptcDebug.clientErr.push', value, '\n', new Error().stack);
        // at this point, we can assume that ptcDebug._errs is defined
        window.ptcDebug!._errs!.push({
          msg: value,
          trace: new Error().stack,
        });
      },
    };
    (historicClientErrors as Array<unknown>).forEach((error) => {
      if (!window) return;
      if (!window.ptcDebug || !window.ptcDebug.errs) {
        console.error('ptcDebug.errs is not defined', error);
        return;
      }
      window.ptcDebug.errs.push(error);
    });
  } catch (e) {
    console.warn('Error pushing to ptcDebug', e);
  }
});
